import React from 'react';
import styled from 'styled-components';
import { Card } from '../Card';

// Trend types
export type TrendDirection = 'up' | 'down' | 'neutral';

// Metric props interface
export interface MetricProps {
  /**
   * Title of the metric
   */
  title: string;
  /**
   * Value of the metric
   */
  value: number | string;
  /**
   * Previous value for comparison
   */
  previousValue?: number | string;
  /**
   * Unit of measurement (e.g., '%', '$', etc.)
   */
  unit?: string;
  /**
   * Trend direction (up, down, neutral)
   */
  trend?: TrendDirection;
  /**
   * Whether up trend is good (green) or bad (red)
   */
  isUpTrendPositive?: boolean;
  /**
   * Percentage change from previous value
   */
  changePercentage?: number;
  /**
   * Icon to display with the metric
   */
  icon?: React.ReactNode;
  /**
   * Additional description or context
   */
  description?: string;
  /**
   * Whether to show the trend indicator
   */
  showTrend?: boolean;
  /**
   * Whether to show the change percentage
   */
  showChangePercentage?: boolean;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
}

// Styled components
const MetricContainer = styled(Card)<{ $clickable?: boolean }>`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: ${props => props.$clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => props.$clickable ? props.theme.shadows.md : props.theme.shadows.sm};
  }
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MetricTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme?.colors?.neutral?.[600] || '#757575'};
  margin: 0;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: ${props => props.theme?.borderRadius?.full || '9999px'};
  background-color: ${props => props.theme?.colors?.neutral?.[100] || '#F5F5F5'};
  color: ${props => props.theme?.colors?.primary?.[500] || '#1890FF'};
`;

const MetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.neutral?.[900] || '#212121'};
  margin-bottom: 0.25rem;
  display: flex;
  align-items: baseline;
`;

const MetricUnit = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${props => props.theme?.colors?.neutral?.[600] || '#757575'};
  margin-left: 0.25rem;
`;

const MetricFooter = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
`;

const TrendIndicator = styled.div<{ $trend: TrendDirection; $isPositive: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => {
    if (props.$trend === 'neutral') return props.theme?.colors?.neutral?.[600] || '#757575';
    return props.$isPositive ?
      (props.$trend === 'up' ? props.theme?.colors?.semantic?.success?.default || '#52C41A' : props.theme?.colors?.semantic?.error?.default || '#FF4D4F') :
      (props.$trend === 'up' ? props.theme?.colors?.semantic?.error?.default || '#FF4D4F' : props.theme?.colors?.semantic?.success?.default || '#52C41A');
  }};
  margin-right: 0.5rem;
`;

const ChangePercentage = styled.span<{ $trend: TrendDirection; $isPositive: boolean }>`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => {
    if (props.$trend === 'neutral') return props.theme?.colors?.neutral?.[600] || '#757575';
    return props.$isPositive ?
      (props.$trend === 'up' ? props.theme?.colors?.semantic?.success?.default || '#52C41A' : props.theme?.colors?.semantic?.error?.default || '#FF4D4F') :
      (props.$trend === 'up' ? props.theme?.colors?.semantic?.error?.default || '#FF4D4F' : props.theme?.colors?.semantic?.success?.default || '#52C41A');
  }};
`;

const MetricDescription = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme?.colors?.neutral?.[600] || '#757575'};
  margin: 0;
  flex: 1;
`;

/**
 * Metric component for displaying KPIs and metrics
 */
const Metric: React.FC<MetricProps> = ({
  title,
  value,
  previousValue,
  unit,
  trend = 'neutral',
  isUpTrendPositive = true,
  changePercentage,
  icon,
  description,
  showTrend = true,
  showChangePercentage = true,
  className,
  onClick,
}) => {
  // Calculate trend and change percentage if not provided
  let calculatedTrend = trend;
  let calculatedChangePercentage = changePercentage;

  if (previousValue !== undefined && typeof value === 'number' && typeof previousValue === 'number') {
    if (calculatedTrend === 'neutral') {
      if (value > previousValue) calculatedTrend = 'up';
      else if (value < previousValue) calculatedTrend = 'down';
    }

    if (calculatedChangePercentage === undefined && previousValue !== 0) {
      calculatedChangePercentage = ((value - previousValue) / Math.abs(previousValue)) * 100;
    }
  }

  return (
    <MetricContainer className={className} $clickable={!!onClick} onClick={onClick}>
      <MetricHeader>
        <MetricTitle>{title}</MetricTitle>
        {icon && <IconContainer>{icon}</IconContainer>}
      </MetricHeader>
      
      <MetricValue>
        {value}
        {unit && <MetricUnit>{unit}</MetricUnit>}
      </MetricValue>
      
      <MetricFooter>
        {showTrend && calculatedTrend !== 'neutral' && (
          <TrendIndicator $trend={calculatedTrend} $isPositive={isUpTrendPositive}>
            {calculatedTrend === 'up' ? '↑' : '↓'}
            {showChangePercentage && calculatedChangePercentage !== undefined && (
              <ChangePercentage $trend={calculatedTrend} $isPositive={isUpTrendPositive}>
                {Math.abs(calculatedChangePercentage).toFixed(1)}%
              </ChangePercentage>
            )}
          </TrendIndicator>
        )}
        {description && <MetricDescription>{description}</MetricDescription>}
      </MetricFooter>
    </MetricContainer>
  );
};

export default Metric;